import { useEffect, useRef, useMemo } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import useBEOImportJobsStore from '../state/beoImportJobs';
import type { BEOJobEntry } from '../state/beoImportJobs';
import { subscribeToBeoImportJob } from '../data/beoImportJobs';
import type { BeoImportJob } from '../data/beoImportJobs';

const ACTIVE_STATUSES = new Set(['queued', 'processing']);

/**
 * Hook that manages BEO import job tracking with realtime subscriptions.
 * Used in NotificationBell and BEOUploadModal.
 */
export function useBEOImportJobs() {
  const jobs = useBEOImportJobsStore((s) => s.jobs);
  const addJob = useBEOImportJobsStore((s) => s.addJob);
  const updateJob = useBEOImportJobsStore((s) => s.updateJob);
  const dismissJob = useBEOImportJobsStore((s) => s.dismissJob);
  const quietAll = useBEOImportJobsStore((s) => s.quietAll);

  // Track active realtime subscriptions
  const channelsRef = useRef<Map<string, RealtimeChannel>>(new Map());

  // Subscribe to active jobs via Supabase Realtime
  useEffect(() => {
    const activeJobs = jobs.filter((j) => ACTIVE_STATUSES.has(j.status));
    const currentChannels = channelsRef.current;

    // Subscribe to new active jobs
    for (const job of activeJobs) {
      if (currentChannels.has(job.jobId)) continue;
      const channel = subscribeToBeoImportJob(job.jobId, (row: BeoImportJob) => {
        updateJob(job.jobId, {
          status: row.status as BEOJobEntry['status'],
          heartbeatMessage: row.heartbeat_message,
          lastHeartbeat: row.last_heartbeat,
          eventCount: row.event_count,
          errorMessage: row.error_message,
        });
      });
      currentChannels.set(job.jobId, channel);
    }

    // Unsubscribe from jobs that are no longer active
    for (const [jobId, channel] of currentChannels) {
      const job = jobs.find((j) => j.jobId === jobId);
      if (!job || !ACTIVE_STATUSES.has(job.status)) {
        channel.unsubscribe();
        currentChannels.delete(jobId);
      }
    }

    return () => {
      // Cleanup all on unmount
      for (const [, channel] of currentChannels) {
        channel.unsubscribe();
      }
      currentChannels.clear();
    };
  }, [jobs, updateJob]);

  const activeCount = useMemo(
    () => jobs.filter((j) => ACTIVE_STATUSES.has(j.status)).length,
    [jobs],
  );

  const unquietCount = useMemo(
    () => jobs.filter((j) => !j.quieted).length,
    [jobs],
  );

  return { jobs, addJob, dismissJob, quietAll, activeCount, unquietCount };
}
